import { Typography } from "antd"

const { Text, Paragraph } = Typography

export const balanceFAQ = [
  {
    question: "How does Balance work?",
    answer: () => (
      <div>
        <Paragraph type="secondary">
          Balance lets you prepay your Chargeback.io account. When an alert is triggered, the charge
          is deducted from your balance instead of being invoiced later.
        </Paragraph>
      </div>
    )
  },
  {
    question: "What happens to my balance if I stop using Chargeback.io?",
    answer: () => (
      <div>
        <Paragraph type="secondary">
          You can request a refund of your remaining balance (up to $10,000 USD) after all of your
          business accounts are fully disenrolled from all alert networks (for example, RDR and
          Ethoca).
        </Paragraph>
        <Paragraph type="secondary">
          To request a refund, contact our support team and ask for a balance refund. We’ll confirm
          disenrollment and process the refund to your original payment method.
        </Paragraph>
      </div>
    )
  },
  {
    question: "Do all my accounts share the same balance?",
    answer: "Yes. All accounts under your Chargeback.io organization share one balance."
  },
  {
    question: "Can I see a detailed balance history?",
    answer: () => (
      <div>
        <Paragraph type="secondary">Yes. To view your balance history:</Paragraph>
        <ol style={{ paddingLeft: "1.5em", marginBottom: 14 }}>
          <li>
            <Text type="secondary">
              Click <Text strong>Account Settings</Text> (lower-left corner)
            </Text>
          </li>
          <li>
            <Text type="secondary">
              Click <Text strong>Settings</Text> in the menu
            </Text>
          </li>
          <li>
            <Text type="secondary">
              In the sidebar, click <Text strong>Billing</Text>
            </Text>
          </li>
        </ol>

        <Paragraph type="secondary">On Billing, you’ll find:</Paragraph>
        <ul style={{ listStyleType: "disc", paddingLeft: "1.5em", marginBottom: 0 }}>
          <li>
            <Text type="secondary">Payment Activity shows balance credits and alert charges</Text>
          </li>
          <li>
            <Text type="secondary">
              Invoice History shows your top-ups, downloadable invoices, and payment method used
            </Text>
          </li>
        </ul>
      </div>
    )
  },
  {
    question: "Can I disable Auto Top-Up?",
    answer:
      "No. Auto Top-Up can’t be turned off. It ensures your alerts continue without interruption."
  },
  {
    question: "What Team roles can manage balances?",
    answer: () => (
      <div>
        <Paragraph type="secondary">Balance permissions depend on a team member’s role:</Paragraph>

        <ol style={{ paddingLeft: "1.5em", marginBottom: 14 }}>
          <li>
            <Text type="secondary">
              <Text strong>Owner:</Text> Full access to balance management
            </Text>
          </li>
          <li>
            <Text type="secondary">
              <Text strong>Administrator:</Text>
              <ul style={{ listStyleType: "disc", marginTop: 8 }}>
                <li>
                  <Text type="secondary">Top up the balance</Text>
                </li>
                <li>
                  <Text type="secondary">Manage Auto Top-Up settings</Text>
                </li>
                <li>
                  <Text type="secondary">Add or remove payment methods</Text>
                </li>
              </ul>
            </Text>
          </li>
          <li>
            <Text type="secondary">
              <Text strong>Managers & View Only:</Text>
              <p style={{ listStyleType: "disc", marginTop: 8 }}>
                <Text type="secondary">
                  These roles can only{" "}
                  <Text type="secondary" italic>
                    view
                  </Text>
                  :
                </Text>
              </p>
              <ul style={{ listStyleType: "disc" }}>
                <li>
                  <Text type="secondary">Payment method(s) on file</Text>
                </li>
                <li>
                  <Text type="secondary">Auto Top-Up settings</Text>
                </li>
                <li>
                  <Text type="secondary">Current balance</Text>
                </li>
              </ul>
            </Text>
          </li>
        </ol>

        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          Managers and View Only team members <Text strong>cannot</Text> top up the balance or
          change any settings.
        </Paragraph>
        <Paragraph type="secondary">
          If you want someone to manage balances, assign them the Administrator role{" "}
          <a
            href="https://help.chargeback.io/en/articles/12782800-adding-team-members-to-your-chargeback-io-account"
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: "underline" }}
          >
            using this guide
          </a>
          .
        </Paragraph>
      </div>
    )
  },
  {
    question: "How is my balance calculated?",
    answer: () => (
      <div>
        <Paragraph type="secondary">
          Your Recommended balance level is recalculated each week based on:
        </Paragraph>

        <ul style={{ listStyleType: "disc", paddingLeft: "1.5em", marginBottom: 14 }}>
          <li>
            <Text type="secondary">How many alerts you received in the past week</Text>
          </li>
          <li>
            <Text type="secondary">Your average weekly alert volume over time</Text>
          </li>
          <li>
            <Text type="secondary">
              How many active business accounts you have under your Chargeback.io organization
            </Text>
          </li>
        </ul>

        <Text type="secondary">
          Right now, you can’t manually set the recommended balance level.
        </Text>
      </div>
    )
  },
  {
    question: "How does Balance work for new users?",
    answer: () => (
      <div>
        <Paragraph type="secondary">
          New users start in Demo Mode to explore the platform until a balance is added. In Demo
          Mode, you won’t receive alerts.
        </Paragraph>
        <Paragraph type="secondary">
          To start receiving alerts, top up your balance to the recommended amount (typically around
          $100 USD).
        </Paragraph>
      </div>
    )
  },
  {
    question: "How can I reduce how often my card is charged?",
    answer: () => (
      <div>
        <Paragraph type="secondary">
          You can reduce how often your card is charged by keeping a larger balance.
        </Paragraph>
        <Paragraph type="secondary">
          Auto Top-Up only charges your card when your balance drops to your Minimum level. If you
          add more funds up front, your balance hits the Critical level less often.
        </Paragraph>
        <Paragraph type="secondary">
          Your Critical level is personalized based on your alert activity and the number of active
          accounts, so it may be different from other customers. You can review each top-up in{" "}
          Billing → Payment Activity / Invoice History.
        </Paragraph>
      </div>
    )
  },
  {
    question: "Will I stop receiving alerts if I don’t top up my balance?",
    answer: () => (
      <div>
        <Paragraph type="secondary">If your balance reaches the Minimum level, we will:</Paragraph>

        <ol style={{ paddingLeft: "1.5em", marginBottom: 14 }}>
          <li>
            <Text type="secondary">Attempt an Auto Top-Up to add funds, and</Text>
          </li>
          <li>
            <Text type="secondary">Send you an email about the low balance.</Text>
          </li>
        </ol>

        <Paragraph type="secondary">
          If Auto Top-Up fails after all attempts, we will disenroll all billing descriptors across
          your accounts from alert networks. Your account will show as Inactive, and you won’t
          receive alerts while descriptors remain disenrolled. This can increase your chargeback
          risk.
        </Paragraph>

        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          To restore alerts, add funds above your Critical level. Your descriptor(s) will
          automatically re-enroll within 1–2 business days.
        </Paragraph>
      </div>
    )
  }
]
